import { useEffect, useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import { RobotsRuleResponse } from 'store/api/apiTypes';
import { isNotFoundApiError } from 'shared/helpers/getApiErrorStatus';
import {
  useGetRobotsTxtRuleQuery,
  useLazyGetRobotsTxtRuleQuery,
} from 'store/api/rulesApiSlice';

/**
 * Hook to fetch Robots.txt technical accessibility rule data for the currently selected project domain.
 * Provides both cached automatic query behavior and a manual trigger (lazy) for on-demand refresh.
 */
export const useRobotsTxtRule = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );

  const activeReport = useAppSelector(state => state.history.activeReport);
  const requestBody = useMemo(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain: selectedProject.domain, reportId: activeReport?.id };
  }, [selectedProject, activeReport]);

  const {
    data: ruleData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetRobotsTxtRuleQuery(
    requestBody as { domain: string; reportId?: string },
    {
      skip: !requestBody,
      // Re-fetch when selected project changes or active report changes
      refetchOnMountOrArgChange: true,
    }
  );

  const [trigger, lazyResult] = useLazyGetRobotsTxtRuleQuery();

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  // Handle errors from primary query
  useEffect(() => {
    if (error && !isNotFoundApiError(error)) {
      handleApiError(error);
    }
  }, [error, handleApiError]);

  usePollingOnError(requestBody, error, refetch, ruleData);

  const typed = ruleData as RobotsRuleResponse | undefined;

  return {
    ruleData: typed,
    isLoading,
    isFetching,
    error,
    hasData: !!ruleData,
    refetch,
    lazyTrigger: (domainOverride?: string) =>
      trigger({ domain: domainOverride || selectedProject?.domain || '' }),
    lazyResult,
    showErrorModal,
    hideErrorModal,
    handleRetry,
  };
};
